import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  ENVIRONMENT_OPTIONS,
  getEnvironmentDisplayName,
  getEnvironmentTokenStorageKey,
  isProductionEnvironment,
  type Environment,
} from '../../../services/environmentConfig';

import { Icon } from '@liftango/ui-icons';
export type { Environment } from '../../../services/environmentConfig';

export {
  getEnvironmentDescriptor,
  getEnvironmentDisplayName,
  isProductionEnvironment,
} from '../../../services/environmentConfig';

interface EnvironmentSelectorProps {
  selectedEnvironment: Environment;
  onEnvironmentChange: (environment: Environment) => void;
  jwtToken: string;
  onJwtTokenChange: (token: string) => void;
}

export const EnvironmentSelector: React.FC<EnvironmentSelectorProps> = ({
  selectedEnvironment,
  onEnvironmentChange,
  jwtToken,
  onJwtTokenChange,
}) => {
  const { t } = useTranslation('map');
  const [showJwtToken, setShowJwtToken] = useState<boolean>(false);

  // Load JWT token from localStorage on mount and when environment changes
  useEffect(() => {
    const storageKey = getEnvironmentTokenStorageKey(selectedEnvironment);
    const savedToken = localStorage.getItem(storageKey) || '';
    if (jwtToken !== savedToken) {
      onJwtTokenChange(savedToken);
    }
  }, [selectedEnvironment]);

  const handleJwtTokenChange = (value: string) => {
    const storageKey = getEnvironmentTokenStorageKey(selectedEnvironment);
    const trimmedToken = value.trim();
    if (trimmedToken) {
      localStorage.setItem(storageKey, trimmedToken);
    } else {
      localStorage.removeItem(storageKey);
    }
    onJwtTokenChange(value);
  };

  // Validate JWT token format (basic check: not empty, reasonable length)
  const isTokenValid = jwtToken.trim().length > 0 && jwtToken.trim().length >= 10;

  const handleEnvironmentChange = (
    _event: React.MouseEvent<HTMLElement>,
    newEnvironment: Environment | null,
  ) => {
    if (newEnvironment !== null) {
      onEnvironmentChange(newEnvironment);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {t('dataTransfer.environment.title')}
      </Typography>

      <ToggleButtonGroup
        value={selectedEnvironment}
        exclusive
        onChange={handleEnvironmentChange}
        aria-label={t('dataTransfer.environment.ariaLabel')}
        fullWidth
        sx={{ mb: 2 }}
      >
        {ENVIRONMENT_OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
        {t('dataTransfer.environment.authentication')}
      </Typography>

      <TextField
        fullWidth
        type={showJwtToken ? 'text' : 'password'}
        label={t('dataTransfer.environment.jwtTokenLabel', { env: getEnvironmentDisplayName(selectedEnvironment) })}
        value={jwtToken}
        onChange={(e) => handleJwtTokenChange(e.target.value)}
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t('dataTransfer.environment.tokenVisibilityAria')}
                onClick={() => setShowJwtToken(!showJwtToken)}
                edge="end"
              >
                {showJwtToken ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      {/* Authentication Status Indicator */}
      {jwtToken.trim() ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isTokenValid ? (
            <>
              <Icon size={16} name="circle-check" style={{ color: '#2e7d32'}} />
              <Typography variant="caption" color="success.main">
                {t('dataTransfer.environment.tokenValid')}
              </Typography>
            </>
          ) : (
            <>
              <Icon size={16} name="circle-exclamation" style={{ color: '#ed6c02'}} />
              <Typography variant="caption" color="warning.main">
                {t('dataTransfer.environment.tokenInvalid')}
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t('dataTransfer.environment.enterToken')}
        </Typography>
      )}

      {isProductionEnvironment(selectedEnvironment) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('dataTransfer.drawer.productionManualJwtNotice')}
        </Alert>
      )}
    </Box>
  );
};

export default EnvironmentSelector;

